import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Tag,
  Button,
  Spin,
  Row,
  Col,
  Layout,
  Typography,
  Divider,
  Badge,
  Progress,
  Empty,
  Statistic,
  Modal,
  Alert,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CompassOutlined,
  RocketOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  StarFilled,
  UserOutlined,
} from "@ant-design/icons";
import { useAlternativeCareerPaths } from "Hooks/AdminHooks/Organization/useAlternativeCareerPaths";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import "./AlternativePaths.scss";
import { ArrowRightOutlined } from "@mui/icons-material";

const { Content } = Layout;
const { Text, Title } = Typography;

// Per Mudassir (May-20): the alternative-paths page can also be mounted under
// the Employee Dashboard (mirror UI, only different API). Caller passes
// `useMeEndpoint` via route props or the parent route can wrap this page.
const AlternativePathsPage = ({ useMeEndpoint = false } = {}) => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const {
    careerPaths,
    loadingCareerPaths,
    fetchAlternativeCareerPaths,
    isApplicable,
    notApplicableMessage,
    hasAlternativeCareerPaths,
    isAiRecommendationAvailable,
    backendMessage,
    loadingPerformanceReview,
    performanceReview,
    fetchPerformanceReview,
    loadingSupervisors,
    supervisors,

    selectedPath,
    selectedSupervisor,
    setSelectedSupervisor,
    selectedJob,
    setSelectedJob,
    modalVisible,
    setModalVisible,

    handleOpenModal,
    handleSubmitTransition,
    handleJobChange
  } = useAlternativeCareerPaths(employeeId, { useMeEndpoint });

  useEffect(() => {
    // /me endpoint doesn't need employeeId from URL; HR path does.
    if (useMeEndpoint || employeeId) {
      fetchAlternativeCareerPaths(employeeId);
      if (employeeId) fetchPerformanceReview(employeeId);
    }
  }, [employeeId, useMeEndpoint]);

  if (loadingCareerPaths || loadingPerformanceReview) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div
          className="loading-container"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            width: "100%",
          }}
        >
          <Spin size="large" tip="Aggregating Career Insights..." />
        </div>
      </DashboardLayout>
    );
  }

  // Executive-employee scenario: career-path feature isn't applicable.
  // Show a clean message screen instead of attempting to render the UI.
  if (!isApplicable) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Content className="career-analytics-container">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "80px 24px",
              maxWidth: 560,
              margin: "0 auto",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at center, #dbeafe 0%, #eff6ff 80%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.8rem",
                marginBottom: "18px",
              }}
            >
              🌟
            </div>
            <Title level={3} style={{ margin: 0, color: "#0f5a2a" }}>
              Career Path is not applicable
            </Title>
            <Text
              type="secondary"
              style={{
                fontSize: "0.95rem",
                lineHeight: 1.55,
                marginTop: "10px",
                maxWidth: 480,
              }}
            >
              {notApplicableMessage ||
                "This employee holds an executive role that spans the organization. The standard career-path progression and alternative trajectories don't apply to org-wide leadership positions."}
            </Text>
            <Button
              icon={<ArrowLeftOutlined />}
              type="link"
              onClick={() => navigate(-1)}
              className="btn-back"
              style={{ marginTop: 20 }}
            >
              Back
            </Button>
          </div>
        </Content>
      </DashboardLayout>
    );
  }

  const paths = careerPaths?.alternativePaths || [];
  const currentPath = careerPaths?.currentPath;
  const employee = careerPaths?.employee;
  const review = performanceReview?.performanceReview;

  // Per Mudassir (May-22) explicit flags replace the earlier heuristic
  // detection. Three cases:
  //   1. hasPaths=true  + aiAvailable=true  → render paths normally
  //   2. hasPaths=true  + aiAvailable=false → render paths, hide AI fields,
  //                                            show banner above the cards
  //   3. hasPaths=false                     → empty state using backendMessage
  const aiUnavailable =
    hasAlternativeCareerPaths === true &&
    isAiRecommendationAvailable === false;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Content className="career-analytics-container">
        {/* --- HEADER --- */}
        <div className="header-luxury">
          <div className="header-main-info">
            <Title level={2} className="m-0">
              Career Trajectory Analytics
            </Title>
            <Text type="secondary" className="m-0">
              Growth strategy for{" "}
              <span className="highlight-text">{employee?.fullName || review?.revieweeName}</span>
            </Text>
          </div>

          <Button
            icon={<ArrowLeftOutlined />}
            type="link"
            onClick={() => navigate(-1)}
            className="btn-back"
          >
            Back
          </Button>
        </div>

        {/* --- PERFORMANCE SCORECARD --- */}
        {review && (
          <div className="performance-scorecard-row">
            <div className="section-label">
              <StarFilled /> Performance Standing
            </div>
            <Card className="performance-main-card" bordered={false}>
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={6} className="rating-summary">
                  <Statistic
                    title="Overall Rating"
                    value={review.performanceRatingCode?.name}
                    prefix={<Badge status="processing" color="#52c41a" />}
                  />
                  <div className="score-badge">
                    <Text strong>{review.totalPerformanceScore}%</Text>
                    <small>Score</small>
                  </div>
                </Col>

                <Col xs={24} md={18}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <div className="mini-metric">
                        <Text type="secondary">Accountability</Text>
                        <Progress
                          percent={review.weightedAccountabilityScore}
                          strokeColor="#1890ff"
                          size="small"
                          format={(percent) => `${percent}/50`}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={8}>
                      <div className="mini-metric">
                        <Text type="secondary">Performance Factors</Text>
                        <Progress
                          percent={(review.weightedPerformanceFactorsScore / 40) * 100}
                          success={{ percent: (review.weightedPerformanceFactorsScore / 40) * 100 }}
                          size="small"
                          format={() => `${review.weightedPerformanceFactorsScore}/40`}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={8}>
                      <div className="mini-metric">
                        <Text type="secondary">Org. Behaviors</Text>
                        <Progress
                          percent={(review.weightedDynamicOrganizationScore / 10) * 100}
                          strokeColor="#52c41a"
                          size="small"
                          format={() => `${review.weightedDynamicOrganizationScore}/10`}
                        />
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </div>
        )}

        {/* --- DOMAINS & SKILLS ROW --- */}
        <div className="analytics-summary-row mt-4">
          <Row gutter={[8, 8]}>
            <Col xs={24} lg={12}>
              <Card className="summary-pill-card" bordered={false}>
                <div className="summary-icon domain-icon">
                  <GlobalOutlined />
                </div>
                <div className="summary-content">
                  <Text className="summary-label">Domain Knowledge of Employee</Text>
                  <div className="summary-tags">
                    {currentPath?.domainKnowledges?.map((domain) => (
                      <Tag key={domain.domainKnowledgeId} className="luxury-tag domain">
                        {domain.name} <small>({domain.industry})</small>
                      </Tag>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="summary-pill-card" bordered={false}>
                <div className="summary-icon skill-icon">
                  <SafetyCertificateOutlined />
                </div>
                <div className="summary-content">
                  <Text className="summary-label">Skills of Employee</Text>
                  <div className="summary-tags">
                    {employee?.employeeSkills?.map((skill) => (
                      <Tag key={skill.skillId} className="luxury-tag skill">
                        {skill.skillName}
                      </Tag>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        {/* --- CURRENT PATH SECTION --- */}
        {currentPath && currentPath.jobTitles && (
          <div className="path-container current-path mt-4">
            <div className="section-label mb-1">
              <CompassOutlined /> Current Trajectory: <strong>{currentPath.name}</strong>
              <Tag color="gold" className="ms-2" style={{ fontSize: "10px" }}>
                {currentPath.publicId}
              </Tag>
            </div>

            {/* Career Path Level Description */}
            {currentPath.description && (
              <div className="mb-2 px-2">
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  {currentPath.description}
                </Text>
              </div>
            )}

            {/* Domains for Current Path */}
            {currentPath.domainKnowledges?.length > 0 && (
              <div className="mb-4 px-2">
                <div
                  className="text-uppercase mb-2"
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#8c8c8c",
                    letterSpacing: "0.5px",
                  }}
                >
                  Domains
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {currentPath.domainKnowledges.map((dk) => (
                    <Tag
                      key={dk.domainKnowledgeId}
                      color="blue"
                      style={{ borderRadius: "4px", fontSize: "11px" }}
                    >
                      {dk.name} <small style={{ opacity: 0.8 }}>({dk.industry})</small>
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <div className="horizontal-roadmap">
              {currentPath.jobTitles
                ?.sort((a, b) => a.stepOrder - b.stepOrder)
                .map((job) => {
                  const isCurrent = job.isCurrent;
                  return (
                    <div key={job.id} className="roadmap-segment">
                      <div className={`roadmap-node ${isCurrent ? "is-active" : ""}`}>
                        {isCurrent && (
                          <div className="status-tag pulse-active">Current Position</div>
                        )}

                        <Card className="node-card" bordered={false}>
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Title level={5} className="job-name m-0">
                              {job.name}
                            </Title>
                            <Tag className="m-0" style={{ fontSize: "9px" }}>
                              {job.publicId}
                            </Tag>
                          </div>

                          {/* Job Description */}
                          <Text
                            type="secondary"
                            className="d-block mb-2"
                            style={{ fontSize: "11px", lineHeight: "1.3" }}
                          >
                            {job.description}
                          </Text>

                          <Text className="exp-label">
                            {job.maxExperienceRequired
                              ? `${job.minExperienceRequired}-${job.maxExperienceRequired} Years`
                              : `${job.minExperienceRequired}+ Years`}
                          </Text>

                          <Divider className="node-divider" />

                          <div className="skill-grid">
                            {job.requiredSkills?.map((skill) => (
                              <Tag
                                key={skill.skillId}
                                className={`skill-pill ${skill.hasSkill ? "earned" : "missing"}`}
                                icon={
                                  skill.hasSkill ? <CheckCircleFilled /> : <CloseCircleFilled />
                                }
                              >
                                {skill.skillName}
                              </Tag>
                            ))}
                          </div>

                          <div className="node-meta mt-3">
                            <span>
                              <TeamOutlined style={{ marginRight: "4px" }} />
                              {job.employeeCount} in role
                            </span>
                            {job.skillMatchPercentage > 0 && (
                              <span className="ms-auto text-primary" style={{ fontWeight: 600 }}>
                                {Math.round(job.skillMatchPercentage)}% Match
                              </span>
                            )}
                          </div>
                        </Card>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* --- ALTERNATIVE PATHS --- */}
        <div className="section-label mt-5">
          <RocketOutlined /> Transition Opportunities
        </div>

        {/* AI-down banner sits ABOVE the path cards so HR still sees the
            structural data (path name, public id) while knowing the AI
            scoring/insights aren't available right now. */}
        {aiUnavailable && (
          <Alert
            type="warning"
            showIcon
            message="AI temporarily unavailable"
            description={
              backendMessage ||
              "AI-generated fit scores and insights couldn't be produced right now. The paths below show what alternatives exist — please check back once the AI service is restored for scoring."
            }
            style={{ borderRadius: 14, marginTop: 8, marginBottom: 16 }}
          />
        )}

        {!hasAlternativeCareerPaths ? (
          <Empty
            description={
              backendMessage || "No Alternative Trajectories Identified"
            }
          />
        ) : paths.length === 0 ? (
          <Empty description="No Alternative Trajectories Identified" />
        ) : (
          <Row gutter={[24, 24]}>
            {paths.map((path) => {
              // Per Mudassir (May-20) AI-powered schema:
              //   fitPercentage     replaces matchPercentage
              //   isRecommended     replaces canTransition
              //   reason            AI's narrative explanation
              //   estimatedTransitionMonths
              //   transferableSkills, missingSkills, domainGaps
              //   skillRecommendations[{skill, course, platform, priority}]
              //   transitionPlan[{phase, title, durationMonths, actions[]}]
              // When AI is unavailable, fitPercentage / isRecommended /
              // estimatedTransitionMonths come back null. Treat them as
              // "no signal" rather than rendering 0% / no-recommendation.
              // Per Mudassir (May-23): renamed to `fitScore` for consistency
              // across the 3 matching endpoints. Keep legacy aliases as fallback.
              const fitPct =
                path.fitScore ?? path.fitPercentage ?? path.matchPercentage;
              const isRecommended = path.isRecommended ?? path.canTransition;
              const hasFitPct = fitPct != null;
              return (
                <Col xs={24} lg={12} xxl={8} key={path.id}>
                  <Card
                    className={`modern-path-card h-100 ${
                      isRecommended ? "can-transition" : ""
                    }`}
                  >
                    {/* Career Path Header */}
                   <div className="card-header-glass">
  <div className="d-flex flex-column">
    {/* Use align-items-center to keep the title and button on the same horizontal line */}
    <div className="d-flex align-items-center gap-2">
      <Title level={5} className="m-0">
        {path.name}
      </Title>

      {isRecommended && (
        <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>
          ✨ AI Recommended
        </Tag>
      )}

      {/* HR-only action: the employee mirror (useMeEndpoint) is read-only and
          has no employeeId in the URL, so the transition button is hidden there. */}
      {isRecommended && !useMeEndpoint && (
        <Button
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={() => handleOpenModal(path, employeeId)}
          className="btn-primary"
          style={{
            fontSize: "11px",
            height: "24px",
            padding: "0 10px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          Start Transition
        </Button>
      )}
    </div>

    <Text type="secondary" className="small">
      ID: {path.publicId}
      {path.estimatedTransitionMonths != null && (
        <>
          {" "}· <strong>~{path.estimatedTransitionMonths} mo</strong> to transition
        </>
      )}
    </Text>
  </div>

  {hasFitPct ? (
    <div className="match-score">
      <span className="score-text">{Math.round(fitPct)}%</span>
      <span className="score-label">Fit</span>
    </div>
  ) : null}
</div>

                    <div className="card-body-content px-3 mt-3">
                      {/* AI reasoning — explains why this path was suggested. */}
                      {path.reason && (
                        <div
                          style={{
                            background: "#f0f9ff",
                            borderLeft: "3px solid #1890ff",
                            borderRadius: 8,
                            padding: "10px 12px",
                            marginBottom: 12,
                            fontSize: 12,
                            color: "#1e3a5f",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>🤖 AI Insight:</strong> {path.reason}
                        </div>
                      )}

                      {/* Transferable skills (you bring) + Missing skills (to develop) */}
                      {(path.transferableSkills?.length > 0 ||
                        path.missingSkills?.length > 0) && (
                        <Row gutter={[8, 8]} className="mb-3">
                          {path.transferableSkills?.length > 0 && (
                            <Col xs={24} sm={12}>
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: "#0f5a2a",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  marginBottom: 4,
                                }}
                              >
                                ✓ You Bring
                              </div>
                              <div className="d-flex flex-wrap gap-1">
                                {path.transferableSkills.map((s, i) => (
                                  <Tag
                                    key={`t-${i}`}
                                    color="green"
                                    style={{ fontSize: 10, margin: 0 }}
                                  >
                                    {s}
                                  </Tag>
                                ))}
                              </div>
                            </Col>
                          )}
                          {path.missingSkills?.length > 0 && (
                            <Col xs={24} sm={12}>
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: "#92400e",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  marginBottom: 4,
                                }}
                              >
                                ↑ To Develop
                              </div>
                              <div className="d-flex flex-wrap gap-1">
                                {path.missingSkills.map((s, i) => (
                                  <Tag
                                    key={`m-${i}`}
                                    color="orange"
                                    style={{ fontSize: 10, margin: 0 }}
                                  >
                                    {s}
                                  </Tag>
                                ))}
                              </div>
                            </Col>
                          )}
                        </Row>
                      )}

                      {/* Domain gaps (high level domains the employee lacks) */}
                      {path.domainGaps?.length > 0 && (
                        <div className="mb-3">
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#7a4a16",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              marginBottom: 4,
                            }}
                          >
                            🌐 Domain Gaps
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            {path.domainGaps.map((d, i) => (
                              <Tag
                                key={`d-${i}`}
                                style={{
                                  fontSize: 10,
                                  margin: 0,
                                  background: "#fff7ed",
                                  borderColor: "#fcd9a3",
                                  color: "#7a4a16",
                                }}
                              >
                                {d}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Skill recommendations (recommended courses w/ priority) */}
                      {path.skillRecommendations?.length > 0 && (
                        <div className="mb-3">
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#5b21b6",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              marginBottom: 6,
                            }}
                          >
                            📚 Recommended Learning
                          </div>
                          <div className="d-flex flex-column gap-1">
                            {path.skillRecommendations.map((rec, i) => (
                              <div
                                key={`r-${i}`}
                                style={{
                                  background: "#f5f3ff",
                                  border: "1px solid #ddd6fe",
                                  borderRadius: 8,
                                  padding: "6px 10px",
                                  fontSize: 11,
                                  lineHeight: 1.4,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <strong style={{ color: "#5b21b6" }}>{rec.skill}</strong>
                                  <div style={{ color: "#5b6f8c" }}>
                                    {rec.course} · <em>{rec.platform}</em>
                                  </div>
                                </div>
                                <Tag
                                  color={
                                    rec.priority === "High"
                                      ? "red"
                                      : rec.priority === "Medium"
                                        ? "orange"
                                        : "default"
                                  }
                                  style={{ fontSize: 9, margin: 0, fontWeight: 700 }}
                                >
                                  {rec.priority}
                                </Tag>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Transition plan — multi-phase roadmap with actions */}
                      {path.transitionPlan?.length > 0 && (
                        <div className="mb-3">
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#0f5a2a",
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              marginBottom: 6,
                            }}
                          >
                            🗺️ Transition Plan
                          </div>
                          <div className="d-flex flex-column gap-2">
                            {path.transitionPlan.map((phase) => (
                              <div
                                key={`p-${phase.phase}`}
                                style={{
                                  background: "#f0fdf4",
                                  border: "1px solid #bbf7d0",
                                  borderLeft: "3px solid #2b7e3a",
                                  borderRadius: 8,
                                  padding: "8px 12px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 4,
                                  }}
                                >
                                  <strong style={{ fontSize: 12, color: "#0f5a2a" }}>
                                    Phase {phase.phase}: {phase.title}
                                  </strong>
                                  <Tag color="green" style={{ fontSize: 9, margin: 0 }}>
                                    {phase.durationMonths} mo
                                  </Tag>
                                </div>
                                {phase.actions?.length > 0 && (
                                  <ul
                                    style={{
                                      margin: 0,
                                      paddingLeft: 18,
                                      fontSize: 11,
                                      color: "#3e5a3e",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {phase.actions.map((a, i) => (
                                      <li key={i}>{a}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Career Path Description */}
                      {path.description && (
                        <div className="mb-3">
                          <Text
                            type="secondary"
                            style={{ fontSize: "12px", display: "block", lineHeight: "1.4" }}
                          >
                            {path.description}
                          </Text>
                        </div>
                      )}

                      {/* Domains Section */}
                      {path.domainKnowledges?.length > 0 && (
                        <div className="mb-3">
                          <div
                            className="text-uppercase mb-1"
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#8c8c8c",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Domains
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            {path.domainKnowledges.map((dk) => (
                              <Tag
                                key={dk.domainKnowledgeId}
                                color="blue"
                                className="m-0"
                                style={{ fontSize: "10px", borderRadius: "4px" }}
                              >
                                {dk.name}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="path-vertical-steps mt-2">
                      {path.jobTitles?.map((job) => (
                        <div
                          key={job.id}
                          className={`vertical-step ${job.isSuggested ? "Suggested" : ""}`}
                        >
                          <div className="step-indicator">
                            {job.meetsExperienceRequirement ? (
                              <CheckCircleFilled className="text-success" />
                            ) : (
                              <div className="lock-dot" />
                            )}
                          </div>

                          <div className="step-content">
                            {/* Job Title & Employee Count */}
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ flex: 1 }}>
                                <Text strong className="d-block">
                                  {job.name}
                                </Text>
                                <div className="d-flex align-items-center gap-2 mt-1">
                                  <Tag className="m-0" style={{ fontSize: "10px" }}>
                                    {job.publicId}
                                  </Tag>
                                  {/* Employee Count */}
                                  <span style={{ fontSize: "11px", color: "#8c8c8c" }}>
                                    <UserOutlined style={{ marginRight: "4px" }} />
                                    {job.employeeCount || 0} Peers
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Job Description */}
                            <Text type="secondary" className="d-block my-2 small italic">
                              {job.description}
                            </Text>

                            {/* Experience Requirements */}
                            <div className="experience-requirement mb-2">
                              <Badge
                                status={job.meetsExperienceRequirement ? "success" : "default"}
                                text={
                                  <Text className="small">
                                    Exp:{" "}
                                    {job.maxExperienceRequired
                                      ? `${job.minExperienceRequired} - ${job.maxExperienceRequired} years`
                                      : `${job.minExperienceRequired}+ Years`}
                                  </Text>
                                }
                              />
                            </div>

                            {/* Required Skills */}
                            <div className="d-flex flex-wrap gap-1 mt-2">
                              {job.requiredSkills?.length > 0 ? (
                                job.requiredSkills.map((skill) => (
                                  <Tag
                                    key={skill.skillId}
                                    className={`modern-tag ${skill.hasSkill ? "has" : "needs"}`}
                                  >
                                    {skill.skillName}
                                  </Tag>
                                ))
                              ) : (
                                <Text type="secondary" className="small">
                                  No specific skills required
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
    <Modal
  title={`Transition to ${selectedPath?.name}`}
  open={modalVisible}
  onCancel={() => setModalVisible(false)}
  onOk={() => handleSubmitTransition()}
  okText="Confirm Transition"
  okButtonProps={{
    disabled:
      !selectedJob ||
      !selectedSupervisor ||
      loadingSupervisors ||
      supervisors.length === 0,
    className: "btn-primary",
  }}
>
  {/*  STEP 1: JOB TITLE */}
  <div className="mb-3">
    <Text strong>Select Job Title</Text>

    <Select
      placeholder="Select Job Title"
      style={{ width: "100%" }}
      value={selectedJob}
      onChange={(value) => handleJobChange(value)}
      options={selectedPath?.jobTitles?.map((job) => ({
        value: job.id,
        label: (
          <div className="d-flex justify-content-between">
            <span>{job.name}</span>
            {job.isSuggested && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                Recommended
              </Tag>
            )}
          </div>
        ),
      }))}
    />
  </div>

  {/*  STEP 2: SUPERVISOR FLOW */}
  {!selectedJob ? (
    <Alert
      type="info"
      message="Please select a job title first"
      showIcon
    />
  ) : loadingSupervisors ? (
    <div className="text-center py-3">
      <Spin />
    </div>
  ) : supervisors.length === 0 ? (
    <Alert
      type="warning"
      message="No supervisors available"
      description="No supervisors found for the selected job title."
      showIcon
    />
  ) : (
    <div className="mb-3">
      <Text strong>Select Supervisor</Text>

   <Select
  placeholder="Select Supervisor"
  style={{ width: "100%" }}
  value={selectedSupervisor}
  onChange={(value) => setSelectedSupervisor(value)}
  optionLabelProp="label" // 👈 important for selected display
>
  {supervisors.map((sup) => (
    <Select.Option
      key={sup.id}
      value={sup.id}
      label={sup.fullName} 
    >
      <div className="d-flex flex-column">
        {/* 👤 Name */}
        <span style={{ fontWeight: 500 }}>{sup.fullName}</span>

        {/* 💼 Job + Designation */}
        <span style={{ fontSize: 12, color: "#888" }}>
          {sup.jobTitleName} • {sup.designationName}
        </span>
      </div>
    </Select.Option>
  ))}
</Select>
    </div>
  )}
</Modal>
      </Content>
    </DashboardLayout>
  );
};

AlternativePathsPage.propTypes = {
  // When true, the page calls /api/CareerPath/me/career-path/alternatives
  // (Employee Dashboard); otherwise the HR-side /{employeeId}/ variant.
  useMeEndpoint: PropTypes.bool,
};

export default AlternativePathsPage;
